import { FormInstance, message } from 'antd';
import axios from 'axios';
import { BASE_URL, CONTENT_API_PATH, REVENUE_API_PATH } from 'constants/api';
import { ContentListInterface, LibraryInterface } from 'content/library/types/contentType';
import { contentService, contractService, seasonService, seriesService } from 'content/service';
import {
  ContentInterface,
  DubbingInterface,
  SubtitleInterface,
  ThumbnailInterface,
} from 'content/type';
import { SeriesInterface } from 'content/type/seriesType';
import { ContractInterface } from 'contract/type';
import dayjs from 'dayjs';
import { ApiService } from 'service/api/apiService';
import { contentApiService } from 'service/api/contentApiService';
import { peopleApiService } from 'service/api/peopleApiService';
import { RevenueInterface } from 'src/domain/revenue/revenue-dashboard/types/revenueDashboardType';
import { accountStore } from 'store/accountStore';
import { FetchResult } from 'type/apiType';
import { PeopleInterface } from 'type/peopleType';
import { SeasonInterface } from 'type/seasonType';
import { libraryStore } from '../stores/libraryStore';

class LibraryService {
  constructor(private apiService: ApiService<ContentInterface>) {}

  async fetch(query: URLSearchParams): Promise<FetchResult<ContentListInterface>> {
    return (await this.apiService.fetch(query)) as unknown as FetchResult<ContentListInterface>;
  }

  async fetchById(id: number, query?: Record<string, string>): Promise<ContentInterface> {
    return await this.apiService.fetchById(id, query);
  }

  async fetchByIds(
    ids: string,
    queryParams?: Record<string, string>
  ): Promise<FetchResult<ContentInterface>> {
    if (ids.length === 0) {
      return {
        results: [],
        count: 0,
        next: null,
        previous: null,
        page_next: null,
        page_previous: null,
        page_size: 0,
      };
    }
    const response = await axios.get(`${BASE_URL}${CONTENT_API_PATH}`, {
      params: { ids, ...queryParams },
    });
    return response.data;
  }

  async update(id: number, content: Partial<ContentInterface>): Promise<void> {
    await this.apiService.update(id, content);
  }

  // Video Transcode
  async videoTranscode(contentId: number): Promise<void> {
    const response = await axios.post(
      `${BASE_URL}${CONTENT_API_PATH}${contentId}/video-transcode/`
    );
    return response.data;
  }

  async videoTranscodeWithEmbeddedSubtitle(contentId: number): Promise<void> {
    const response = await axios.post(
      `${BASE_URL}${CONTENT_API_PATH}${contentId}/embedded-subtitle-transcode/`
    );
    return response.data;
  }

  async updateDuration(contentId: number): Promise<void> {
    const response = await axios.post(
      `${BASE_URL}${CONTENT_API_PATH}${contentId}/update-content-duration/`
    );
    return response.data;
  }

  // Subtitle
  async fetchSubtitleList(contentId: number): Promise<SubtitleInterface[]> {
    const response = await axios.get(`${BASE_URL}${CONTENT_API_PATH}${contentId}/subtitle/`);
    return response.data;
  }

  public async uploadSubtitle(contentId: number, subtitles: SubtitleInterface[]): Promise<void> {
    await Promise.all(
      subtitles.map(async (subtitle: SubtitleInterface) => {
        const formData = new FormData();
        Object.entries(subtitle).forEach(([key, value]) => {
          formData.append(key, value);
        });
        const response = await axios.post(
          `${BASE_URL}${CONTENT_API_PATH}${contentId}/subtitle/`,
          formData
        );
        return response.data;
      })
    );
  }

  public async updateSubtitle(contentId: number, subtitles: SubtitleInterface[]): Promise<void> {
    await Promise.all(
      subtitles.map(async (subtitle: SubtitleInterface) => {
        const formData = new FormData();
        Object.entries(subtitle).forEach(([key, value]) => {
          formData.append(key, value);
        });
        const response = await axios.patch(
          `${BASE_URL}${CONTENT_API_PATH}${contentId}/subtitle/${subtitle.id}/`,
          formData
        );
        return response.data;
      })
    );
  }

  // Dubbing
  async fetchDubbingList(contentId: number): Promise<FetchResult<DubbingInterface>> {
    const response = await axios.get(`${BASE_URL}${CONTENT_API_PATH}${contentId}/dubbing/`);
    return response.data;
  }

  public async uploadDubbing(contentId: number, dubbings: DubbingInterface[]): Promise<void> {
    await Promise.all(
      dubbings.map(async (dubbing: DubbingInterface) => {
        if (dubbing.id < 0 && dubbing.file) {
          const formData = new FormData();
          Object.entries(dubbing).forEach(([key, value]) => {
            formData.append(key, value);
          });
          const response = await axios.post(
            `${BASE_URL}${CONTENT_API_PATH}${contentId}/dubbing/`,
            formData
          );
          return response.data;
        }
      })
    );
    window.location.reload();
  }

  // Thumbnail
  async fetchThumbnailList(contentId: number): Promise<ThumbnailInterface[]> {
    return await this.apiService.fetchChildItemsByParentId<ThumbnailInterface[]>(
      contentId,
      'thumbnail/'
    );
  }

  async updateThumbnail(contentId: number, thumbnail: ThumbnailInterface): Promise<void> {
    const response = await axios.patch(
      `${BASE_URL}${CONTENT_API_PATH}${contentId}/thumbnail/${thumbnail.id}/`,
      thumbnail
    );
    return response.data;
  }

  public async uploadThumbnail(contentId: number, thumbnails: ThumbnailInterface[]): Promise<void> {
    await Promise.all(
      thumbnails.map(async (thumbnail: ThumbnailInterface) => {
        const formData = new FormData();
        if (thumbnail.originFileObj) {
          formData.append('file', thumbnail.originFileObj);
        }
        if (thumbnail.is_representative) {
          formData.append('is_representative', thumbnail.is_representative.toString());
        }

        if (thumbnail.language) {
          formData.append('language', thumbnail.language);
        }

        if (thumbnail.orientation) {
          formData.append('orientation', thumbnail.orientation);
        }

        const response = await axios.post(
          `${BASE_URL}${CONTENT_API_PATH}${contentId}/thumbnail/`,
          formData
        );
        return response.data;
      })
    );
  }

  public async removeThumbnail(contentId: number, thumbnails: ThumbnailInterface[]): Promise<void> {
    await Promise.all(
      thumbnails.map(async (thumbnail: ThumbnailInterface) => {
        const response = await axios.delete(
          `${BASE_URL}${CONTENT_API_PATH}${contentId}/thumbnail/${thumbnail.id}/`
        );
        return response.data;
      })
    );
  }

  // Revenue
  async fetchRevenueShareData(
    queryString: Record<string, string>
  ): Promise<FetchResult<RevenueInterface>> {
    const qs = Object.entries(queryString)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    const response = await axios.get(`${BASE_URL}${REVENUE_API_PATH}?${qs}`);
    return response.data;
  }

  public async deleteContents(ids: number[]): Promise<void> {
    await axios.delete(`${BASE_URL}${CONTENT_API_PATH}`, { data: { ids } });
  }

  public async deleteContent(id: number): Promise<void> {
    await axios.delete(`${BASE_URL}${CONTENT_API_PATH}${id}/`);
  }

  public async bulkDistribution(ids: number[], contentConsumerName: string): Promise<void> {
    await axios.post(`${BASE_URL}${CONTENT_API_PATH}bulk-distribution/`, {
      ids,
      content_consumer_name: contentConsumerName,
    });
  }

  public async deleteSubtitle(contentId: number, subtitleId: number): Promise<void> {
    const response = await axios.delete(
      `${BASE_URL}${CONTENT_API_PATH}${contentId}/subtitle/${subtitleId}/`
    );
    return response.data;
  }

  public async fetchLibrary(id: number): Promise<LibraryInterface> {
    let library: LibraryInterface | null = libraryStore.getLibrary(id);
    if (library) {
      return library;
    }

    const content = await contentService.fetchById(Number(id));
    let season: SeasonInterface = {} as SeasonInterface;
    let series: SeriesInterface = {} as SeriesInterface;

    if (content.season) {
      season = await seasonService.fetchById(Number(content.season));
      series = await seriesService.fetchById(Number(season.series));
    }

    library = { ...content, season, series, thumbnails: [content.thumbnail] };

    const contentPeopleList = await peopleApiService.fetch(
      new URLSearchParams({
        page: '1',
        page_size: '100',
        contents__id: content.id.toString(),
      })
    );
    library.cast = contentPeopleList.results.map((people: PeopleInterface) => people.id);
    libraryStore.setLibrary(id, library);

    return library;
  }

  public async fetchLibraryContract(library: LibraryInterface): Promise<ContractInterface | null> {
    let contract = libraryStore.getContract(library.id);

    if (!contract && library.contract) {
      contract = await contractService.fetchById(library.contract);
      libraryStore.setLibrary(library.id, library, contract);
    }

    return contract;
  }

  public async updateLibrary(id: string, form: FormInstance, customData?: any): Promise<void> {
    try {
      const platformId = accountStore.getAccount()?.platform?.id || '1';
      await form.validateFields();
      const formValues = form.getFieldsValue();
      // Use customData if available, otherwise use form data
      const values = customData ? { ...formValues, ...customData } : formValues;
      const updatedContent: ContentInterface = {
        id: values.id,
        title: values.title,
        title_en: values.title_en,
        synopsis: values.synopsis,
        synopsis_en: values.synopsis_en,
        // Add SEO fields
        seo_title: values.seo_title,
        seo_synopsis: values.seo_synopsis,
        seo_keyword: values.seo_keyword,
        // Add i18n fields
        i18n_title: values.i18n_title,
        i18n_synopsis: values.i18n_synopsis,
        i18n_seo_title: values.i18n_seo_title,
        i18n_seo_synopsis: values.i18n_seo_synopsis,
        i18n_seo_keyword: values.i18n_seo_keyword,
        slug: values.slug,
        genres: values.genres,
        status: values.status,
        dubbing_mode: values.dubbing_mode,
        episode_order: values.episode_order,
        episode_part: values.episode_part || null,
        season: values.season,
        cast: values.cast,
        enhanced_cast: values.enhanced_cast,
        duration: values.duration,
        categories: values.category ? [values.category] : [],
        content_type: values.content_type,
        age_rating: values.age_rating,
        score: values.score ? Number(values.score) : null,
        contents_file: values.contents_file,
        thumbnail: values.thumbnail,
        contract: values.contract,
        mature_content: values.mature_content,
        on_air: values.on_air,
        on_air_date: values.on_air_date
          ? dayjs(values.on_air_date).format('YYYY-MM-DD')
          : undefined,
        drm_source: values.drm_source,
        content_stream_manifests: values.content_stream_manifests,
        produce_country: values.produce_country,
        language: values.language ? values.language : undefined,
        created: values.created,
        modified: values.modified,
        platform_id: Number(platformId),
        tags: values.tags,
        distributions:
          values.distributions?.map((d: string) => ({ content_consumer_name: d })) || [],
        release_date_start: values.release_date_start,
        release_date_end: values.release_date_end,
        cp_from_odx: values.cp_from_odx,
      };
      await this.apiService.update(Number(id), updatedContent);
      message.success('Updated successfully');
    } catch (error) {
      // Propagate API error to be handled by Detail component
      throw error;
    }
  }

  public deleteLibrary(id: number): void {
    libraryStore.removeLibrary(id);
  }
}

export const libraryService = new LibraryService(contentApiService);
